<div class="flex h-full w-80 flex-col overflow-y-auto border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">

  @if (selectedNode(); as node) {
    <!-- ── Nodo seleccionado ─────────────────────────────────────────────── -->
    <div class="flex items-center gap-2.5 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm" [class]="meta[node.nodeType].iconClass">
        <i [class]="meta[node.nodeType].icon"></i>
      </span>
      <div class="min-w-0 flex-1">
        <!-- Título = etiqueta del SUBTIPO ("Evento del CRM"), no la genérica del tipo ("Disparador"). -->
        <p class="truncate text-sm font-semibold text-gray-800 dark:text-white/90">{{ catalogItem()?.label || meta[node.nodeType].label }}</p>
        <p class="truncate text-[11px] text-gray-400 dark:text-gray-500">
          {{ catalogItem() ? meta[node.nodeType].label : meta[node.nodeType].description }}
        </p>
      </div>
      <button type="button" (click)="onDeselect()"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <div class="flex-1 space-y-5 px-4 py-4">
      @if (nodeErrors().length) {
        <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          <p class="mb-1 flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
            <i class="fa-solid fa-triangle-exclamation"></i> Configuración incompleta
          </p>
          <ul class="ml-4 list-disc space-y-0.5">
            @for (error of nodeErrors(); track error.code + error.message) {
              <li class="text-[11px] text-amber-700 dark:text-amber-400">{{ error.message }}</li>
            }
          </ul>
          <p class="mt-1.5 text-[10px] text-amber-600 dark:text-amber-500">
            Puedes guardar igualmente; esto solo impide activar el workflow.
          </p>
        </div>
      }

      <!--
        Paso que el catálogo marca como NO disponible (integración sin configurar). La paleta ya
        impide añadirlos, pero un nodo guardado antes de deshabilitarse —o traído de una plantilla—
        se sigue pudiendo abrir y configurar, y al ejecutarse se salta sin hacer nada. Aquí se dice,
        con el motivo que da el propio backend.
      -->
      @if (catalogItem(); as item) {
        @if (!item.enabled) {
          <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
            <p class="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
              <i class="fa-solid fa-plug-circle-xmark"></i> Paso no disponible
            </p>
            <p class="mt-1 text-[11px] leading-relaxed text-amber-700 dark:text-amber-400">
              {{ item.reason || 'Esta integración todavía no está configurada.' }}
              Al ejecutarse, el flujo se salta este paso y continúa.
            </p>
          </div>
        }
      }

      <!-- Descripción del subtipo tal y como la escribe el backend: dice CUÁNDO se dispara este
           paso y qué hay que configurar. Sin esto el usuario solo ve la etiqueta del tipo. -->
      @if (catalogItem()?.description; as description) {
        <p class="rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-400">
          {{ description }}
        </p>
      }

      <div>
        <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Nombre del paso</label>
        <input type="text"
               class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
               [value]="node.title" [disabled]="readOnly()" [placeholder]="meta[node.nodeType].label"
               (input)="onTitleChange($event)" />
      </div>

      <!-- Un componente por tipo/subtipo. Esto sustituye al panel monolítico de 2.335 líneas. -->
      @switch (node.nodeType) {
        @case ('TRIGGER') { <app-wf-trigger-config [node]="node" /> }
        @case ('CONDITION') { <app-wf-condition-config [node]="node" /> }
        @case ('WAIT') { <app-wf-wait-config [node]="node" /> }
        @case ('ANNOTATION') { <app-wf-annotation-config [node]="node" /> }
        @case ('END') {
          <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
            Termina el recorrido del registro. No tiene salidas ni configuración.
          </p>
        }
        @case ('ACTION') {
          @switch (actionCategory()) {
            @case ('Comunicación') { <app-wf-communication-action-config [node]="node" /> }
            @case ('Tarea y actividad') { <app-wf-task-activity-action-config [node]="node" /> }
            @case ('Pipeline de ventas') { <app-wf-pipeline-action-config [node]="node" /> }
            @case ('Tickets') { <app-wf-ticket-action-config [node]="node" /> }
            @case ('Webhook') { <app-wf-webhook-action-config [node]="node" /> }
            @case ('Integración y código') { <app-wf-code-integration-action-config [node]="node" /> }
            @default { <app-wf-crm-action-config [node]="node" /> }
          }
        }
      }

      @if (node.nodeType !== 'END' && node.nodeType !== 'ANNOTATION') {
        <label class="flex items-center gap-2 border-t border-gray-100 pt-4 text-xs text-gray-700 dark:border-gray-800 dark:text-gray-300">
          <input type="checkbox" class="rounded border-gray-300" [checked]="node.isActive" [disabled]="readOnly()"
                 (change)="onActiveChange($event)" />
          Paso activo
        </label>
      }

      <!-- Escape hatch: editor JSON crudo, plegado. -->
      <details class="border-t border-gray-100 pt-3 dark:border-gray-800">
        <summary class="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          Modo avanzado (JSON)
        </summary>
        <div class="mt-2 space-y-2">
          <p class="text-[11px] text-gray-400 dark:text-gray-500">
            Clave técnica: <span class="font-mono">{{ node.nodeKey }}</span> (no se puede cambiar).
          </p>
          <textarea rows="8"
                    class="w-full rounded-lg border border-gray-300 px-2 py-1.5 font-mono text-[11px] text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-white/90"
                    [value]="rawJson()" [disabled]="readOnly()"
                    (input)="onRawJsonInput(textareaValue($event))"></textarea>
          @if (rawJsonError()) {
            <p class="text-xs text-error-500">{{ rawJsonError() }}</p>
          }
          @if (rawJsonDraft() !== null) {
            <div class="flex gap-2">
              <button type="button"
                      class="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-600 disabled:opacity-40"
                      [disabled]="readOnly()" (click)="applyRawJson()">Aplicar</button>
              <button type="button"
                      class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
                      (click)="discardRawJson()">Descartar</button>
            </div>
          }
        </div>
      </details>
    </div>

    @if (!readOnly()) {
      <div class="flex gap-2 border-t border-gray-100 px-4 py-3 dark:border-gray-700">
        @if (node.nodeType !== 'TRIGGER') {
          <button type="button" (click)="onDuplicateNode()"
                  class="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
            <i class="fa-solid fa-copy mr-1.5"></i> Duplicar
          </button>
        }
        <button type="button" (click)="onDeleteNode()"
                class="flex-1 rounded-lg border border-rose-200 px-3 py-2 text-xs font-medium text-rose-600 transition hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-900/20">
          <i class="fa-solid fa-trash-can mr-1.5"></i> Eliminar
        </button>
      </div>
    }

  } @else if (selectedEdgeId(); as edgeId) {
    <!-- ── Conexión seleccionada ─────────────────────────────────────────── -->
    <div class="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <span class="text-sm font-semibold text-gray-800 dark:text-white/90">Conexión</span>
      <button type="button" (click)="onDeselect()"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
    <div class="flex-1 px-4 py-4">
      <app-wf-edge-config [edgeId]="edgeId" />
    </div>

  } @else {
    <!-- ── Nada seleccionado ─────────────────────────────────────────────── -->
    <div class="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <p class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ workflow()?.name || 'Workflow' }}</p>
      <p class="text-[11px] text-gray-400 dark:text-gray-500">Selecciona un paso o una conexión para editarlo</p>
    </div>
    <div class="flex-1 space-y-4 px-4 py-4">
      <div class="grid grid-cols-2 gap-2">
        <div class="rounded-lg bg-gray-50 p-2.5 dark:bg-white/5">
          <p class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ nodeCount() }}</p>
          <p class="text-[11px] text-gray-400 dark:text-gray-500">Pasos</p>
        </div>
        <div class="rounded-lg bg-gray-50 p-2.5 dark:bg-white/5">
          <p class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ edgeCount() }}</p>
          <p class="text-[11px] text-gray-400 dark:text-gray-500">Conexiones</p>
        </div>
      </div>

      @if (graphErrors().length) {
        <div>
          <p class="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">Avisos del flujo</p>
          <ul class="space-y-1">
            @for (error of graphErrors(); track error.code) {
              <li class="text-[11px] text-gray-500 dark:text-gray-400">{{ error.message }}</li>
            }
          </ul>
        </div>
      } @else {
        <p class="flex items-center gap-1.5 text-xs text-success-500">
          <i class="fa-solid fa-circle-check"></i> El flujo no tiene avisos de estructura.
        </p>
      }

      <p class="text-xs text-gray-400 dark:text-gray-500">
        Arrastra los pasos desde la paleta y conéctalos en el lienzo.
      </p>
    </div>
  }
</div>
